import { Button, DialogDescription, DialogHeader, DialogTitle, Icon } from "@nexus/ui"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import type { SentScriptsMap } from "@/features/marketplace/types/marketplace.types"

function summarizeAlreadySent(scriptIds: string[], sentScriptsMap: SentScriptsMap) {
  const metas = scriptIds
    .map((id) => sentScriptsMap[id])
    .filter((meta): meta is NonNullable<typeof meta> => meta != null)

  const vendorNames = [...new Set(metas.map((m) => m.vendorName))]
  const orderIds = [...new Set(metas.map((m) => m.orderId))]

  return {
    metas,
    singleVendorName: vendorNames.length === 1 ? vendorNames[0] : null,
    singleOrderId: orderIds.length === 1 ? orderIds[0] : null,
    count: scriptIds.length,
  }
}

export interface SendAlreadySentViewProps {
  scriptIds: string[]
  sentScriptsMap: SentScriptsMap
  onClose: () => void
}

export function SendAlreadySentView({
  scriptIds,
  sentScriptsMap,
  onClose,
}: SendAlreadySentViewProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { singleVendorName, singleOrderId, count } = summarizeAlreadySent(scriptIds, sentScriptsMap)

  function handleViewOrder() {
    if (!singleOrderId) return
    onClose()
    void navigate({ to: "/marketplace", search: { tab: "orders", order: singleOrderId } })
  }

  const bodyKey =
    count === 1 && singleVendorName
      ? "personaOpportunities.scripts.sendDialog.allAlreadySentBody_one"
      : singleVendorName
        ? "personaOpportunities.scripts.sendDialog.allAlreadySentBody_sameVendor"
        : "personaOpportunities.scripts.sendDialog.allAlreadySentBody_other"

  return (
    <div className="flex flex-col items-center px-2 pt-2 pb-1 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
        <Icon name="check-circle" size="lg" className="text-success" />
      </span>

      <DialogHeader className="items-center gap-1.5">
        <DialogTitle className="text-base font-medium">
          {t("personaOpportunities.scripts.sendDialog.allAlreadySentTitle")}
        </DialogTitle>
        <DialogDescription className="max-w-[20rem] text-sm leading-relaxed">
          {t(bodyKey, { count, vendorName: singleVendorName ?? "" })}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
        {singleOrderId && (
          <Button size="sm" onClick={handleViewOrder}>
            {t("personaOpportunities.scripts.sentCard.viewOrder")}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </div>
  )
}
